import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, COURSES, EDUCATION_LEVELS, CAREER_GOALS } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLookups } from "../lib/lookups";
import { toast } from "sonner";
import {
  ChevronLeft, Phone, PhoneCall, Mail, Clock, Edit3, Calendar, TrendingUp,
  MessageSquare, MessageCircle, User, Search, Plus, Check, X,
  Tag, UserCheck, PlusCircle, MoreHorizontal, Play, Trash2,
} from "lucide-react";
import "./LeadDetail.css";

// ---------- Helpers ----------
const initials = (name) => {
  if (!name) return "?";
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
};

const daysAgo = (iso) => {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
    if (diff < 1) return "today";
    return `${Math.floor(diff)}d`;
  } catch { return ""; }
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtIST = (iso, withTime = true) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const ist = new Date(d.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const day = String(ist.getDate()).padStart(2, "0");
    const mon = MONTHS_SHORT[ist.getMonth()];
    const yr = ist.getFullYear();
    if (!withTime) return `${day} ${mon} ${yr}`;
    let hr = ist.getHours(); const min = String(ist.getMinutes()).padStart(2, "0");
    const ampm = hr >= 12 ? "PM" : "AM";
    hr = hr % 12 || 12;
    return `${day} ${mon} ${yr}, ${String(hr).padStart(2, "0")}:${min} ${ampm}`;
  } catch { return iso; }
};

const OUTCOME_LABELS = {
  interested: "Interested",
  not_interested: "Not Interested",
  not_answering: "Not Answering",
  busy: "Busy",
  wrong_number: "Wrong Number",
  callback_requested: "Callback Requested",
  voicemail: "Voicemail",
  other: "Other",
};

const OUTCOME_EMOJI = {
  interested: "✅", not_interested: "❌", not_answering: "🔕", busy: "📵",
  wrong_number: "🚫", callback_requested: "🔁", voicemail: "📼", other: "➖",
};

const durationDisplay = (m) => {
  if (!m) return "0 min";
  const mins = Math.floor(m);
  const secs = Math.round((m - mins) * 60);
  if (mins === 0) return `${secs} sec`;
  if (secs === 0) return `${mins} min`;
  return `${mins} min ${secs} sec`;
};

const SOURCE_LABEL = {
  "meta-fb": "Meta Facebook", "meta-ig": "Meta Instagram",
  google: "Google Ads", offline: "Offline", whatsapp: "WhatsApp",
  inbound_call: "Inbound Call",
};

// ---------- Main ----------
export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const { stages: stageLookups } = useLookups();
  const [detail, setDetail] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [tab, setTab] = useState("details");
  const [modal, setModal] = useState(null); // 'call' | 'note' | 'followup' | 'stage' | null
  const [fuFilter, setFuFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialing, setDialing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, t, f] = await Promise.all([
        api.get(`/leads/${leadId}/detail`),
        api.get(`/leads/${leadId}/timeline`),
        api.get(`/leads/${leadId}/followups`),
      ]);
      setDetail(d.data);
      setTimeline(t.data.activities || []);
      setFollowups(f.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load lead");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/users/staff").then((r) => setStaffList(r.data)).catch(() => {});
  }, []);

  const saveName = async () => {
    const trimmed = nameValue.trim();
    setEditingName(false);
    if (trimmed === (detail?.lead?.name || "")) return;
    try {
      await api.patch(`/leads/${leadId}`, { name: trimmed });
      setDetail((d) => ({ ...d, lead: { ...d.lead, name: trimmed } }));
      toast.success("Name updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update name");
    }
  };

  const assignLead = async (userId) => {
    setAssigning(true);
    try {
      await api.patch(`/leads/${leadId}/assign`, { assigned_to: userId || null });
      setDetail((d) => ({ ...d, lead: { ...d.lead, assigned_to: userId || null } }));
      toast.success(userId ? "Lead assigned" : "Lead unassigned");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const placeCall = async () => {
    setDialing(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/mcube-call`);
      toast.success(data.mock ? "Call initiated (mock mode — no MCube account connected yet)" : "Call initiated");
      // The call's outcome (answered/recording/etc.) arrives async via MCube's webhook
      // (or the mock simulator), so poll briefly to pick up the timeline entry once it lands.
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Call failed");
    } finally {
      setDialing(false);
    }
  };

  const deleteLead = async () => {
    if (!window.confirm(`Permanently delete "${detail?.lead?.name || "this lead"}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      navigate("/leads");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return <div className="ld-loading" data-testid="lead-detail-loading">Loading lead…</div>;
  }
  if (!detail) {
    return (
      <div className="ld-error" data-testid="lead-detail-error">
        Lead not found. <Link to="/leads">Back to Leads</Link>
      </div>
    );
  }

  const lead = detail.lead;
  const stats = detail.stats;
  const stageOrder = stageLookups && stageLookups.length ? stageLookups.map((s) => s.name) : [];
  const currentStageIdx = stageOrder.indexOf(lead.stage);

  const filteredFollowups = followups.filter((f) => fuFilter === "all" || f.status === fuFilter);

  return (
    <div className="ld-root" data-testid="lead-detail-page">
      {/* Top nav */}
      <div className="ld-topnav">
        <Link to="/leads" className="ld-back-btn" data-testid="lead-detail-back-btn">
          <ChevronLeft size={13} /> Back to Leads
        </Link>
        <div className="ld-breadcrumb">
          <span>Leads</span><span>›</span>
          <span className="ld-crumb-current" data-testid="lead-detail-crumb-name">{lead.name || "—"}</span>
        </div>
      </div>

      {/* Lead header */}
      <div className="ld-header">
        <div className="ld-header-top">
          <div className="ld-avatar" data-testid="lead-detail-avatar">{initials(lead.name)}</div>
          <div style={{ flex: 1 }}>
            {(() => {
              const canEditName = me?.role === "admin" || lead.assigned_to === me?.id;
              return editingName ? (
                <input
                  className="ld-lead-name-input"
                  value={nameValue}
                  autoFocus
                  onChange={(e) => setNameValue(e.target.value)}
                  onBlur={saveName}
                  onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                />
              ) : (
                <div
                  className="ld-lead-name"
                  data-testid="lead-detail-name"
                  title={canEditName ? "Click to edit name" : undefined}
                  style={{ cursor: canEditName ? "pointer" : "default" }}
                  onClick={() => { if (canEditName) { setNameValue(lead.name || ""); setEditingName(true); } }}
                >
                  {lead.name || (canEditName ? <span style={{ opacity: 0.5 }}>Add name…</span> : "—")}
                  {canEditName && <Edit3 size={12} style={{ marginLeft: 6, opacity: 0.5 }} />}
                </div>
              );
            })()}
            <div className="ld-badges">
              <span className="ld-stage-badge" data-testid="lead-detail-stage-badge">{lead.stage || "—"}</span>
              <span className="ld-active-badge">● Active</span>
              {lead.category && (
                <span className="ld-category-badge" data-testid="lead-detail-category-badge">{lead.category}</span>
              )}
            </div>
            <div className="ld-contact-row">
              {lead.phone && (
                <a href={`tel:${lead.phone}`} className="ld-contact-item" data-testid="lead-detail-phone">
                  <Phone size={11} /> {lead.phone}
                </a>
              )}
              {lead.phone && (
                <button
                  className="ld-contact-item ld-call-btn"
                  onClick={placeCall}
                  disabled={dialing}
                  data-testid="lead-detail-mcube-call-btn"
                >
                  <PhoneCall size={11} /> {dialing ? "Calling…" : "Call"}
                </button>
              )}
              {lead.email && (
                <a href={`mailto:${lead.email}`} className="ld-contact-item" data-testid="lead-detail-email">
                  <Mail size={11} /> {lead.email}
                </a>
              )}
              <span className="ld-contact-item">
                <Clock size={11} /> Added {fmtIST(lead.created_at, false)}
                <span className="ld-days-badge">{daysAgo(lead.created_at)}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="ld-stats-strip">
          <div className="ld-stat-group">
            <div className="ld-stat-label">Lead Score</div>
            <div className="ld-score-wrap">
              <div className="ld-score-number" data-testid="lead-detail-score">{stats.score}</div>
              <div className="ld-score-right">
                <div className="ld-score-bar-track">
                  <div className="ld-score-bar-fill" style={{ width: `${stats.score}%` }} />
                </div>
                <div className="ld-score-sublabel">out of 100</div>
              </div>
            </div>
          </div>
          <div className="ld-stat-group">
            <div className="ld-stat-label">Communication</div>
            <div className="ld-comm-rows">
              <CommRow icon={<Mail size={11} />} cls="email" label="Email Sent" count={stats.comm.email} />
              <CommRow icon={<MessageSquare size={11} />} cls="sms" label="SMS Sent" count={stats.comm.sms} />
              <CommRow icon={<MessageCircle size={11} />} cls="whatsapp" label="WhatsApp" count={stats.comm.whatsapp} />
            </div>
          </div>
          <div className="ld-stat-group">
            <div className="ld-stat-label">Calls</div>
            <div className="ld-tel-rows">
              <TelRow color="inbound" label="Inbound" count={stats.calls.inbound} />
              <TelRow color="outbound" label="Outbound" count={stats.calls.outbound} />
              <TelRow color="missed" label="Missed / NA" count={stats.calls.missed} />
            </div>
          </div>
          <div className="ld-stat-group">
            <div className="ld-meta-rows">
              <div>
                <div className="ld-meta-label">Assigned To</div>
                <div className="ld-meta-value" style={{ marginBottom: 4 }}>
                  {staffList.find((s) => s.id === lead.assigned_to)?.name || (lead.assigned_to ? "—" : "Unassigned")}
                </div>
                <select
                  data-testid="lead-detail-assigned"
                  disabled={assigning}
                  value={lead.assigned_to || ""}
                  onChange={(e) => assignLead(e.target.value)}
                  style={{ fontSize: 11, padding: "2px 4px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.2)", background: "#1A1A2E", color: "#94A3B8", cursor: "pointer", maxWidth: 130 }}
                >
                  <option value="">Unassigned</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>{s.name || s.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="ld-meta-label">Lead Source</div>
                <span className="ld-source-chip" data-testid="lead-detail-source">
                  <Search size={10} /> {SOURCE_LABEL[lead.source] || lead.source || "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="ld-action-bar">
          <button className="ld-action-btn blue" onClick={() => setModal("call")} data-testid="lead-detail-log-call-btn">
            <Phone size={13} /> Log Call
          </button>
          <button className="ld-action-btn purple" onClick={() => setModal("note")} data-testid="lead-detail-add-note-btn">
            <Edit3 size={13} /> Add Note
          </button>
          <button className="ld-action-btn green" onClick={() => setModal("followup")} data-testid="lead-detail-add-followup-btn">
            <Calendar size={13} /> Add Follow-up
          </button>
          <button className="ld-action-btn" onClick={() => setModal("stage")} data-testid="lead-detail-change-stage-btn">
            <TrendingUp size={13} /> Change Sub-Stage
          </button>
          {me?.role === "admin" && (
            <button className="ld-action-btn red" onClick={deleteLead} data-testid="lead-detail-delete-btn">
              <Trash2 size={13} /> Delete Lead
            </button>
          )}
        </div>
      </div>

      {/* Stage progress */}
      {stageOrder.length > 0 && (
        <div className="ld-stage-progress" data-testid="lead-detail-stage-progress">
          <div className="ld-stages-track">
            {stageOrder.map((sn, i) => {
              const done = currentStageIdx >= 0 && i < currentStageIdx;
              const active = i === currentStageIdx;
              const showLine = i < stageOrder.length - 1;
              return (
                <React.Fragment key={sn}>
                  <div className="ld-stage-node">
                    <div className={`ld-stage-circle ${done ? "done" : ""} ${active ? "active" : ""}`}>
                      {done ? "✓" : active ? "●" : i + 1}
                    </div>
                    <div className={`ld-stage-name ${done ? "done" : ""} ${active ? "active" : ""}`}>{sn}</div>
                  </div>
                  {showLine && <div className={`ld-stage-line ${done ? "done" : ""}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="ld-tabs-bar">
        {[
          { id: "details", label: "Lead Details" },
          { id: "timeline", label: "Timeline" },
          { id: "followups", label: "Follow-ups" },
        ].map((t) => (
          <button
            key={t.id}
            className={`ld-tab-btn ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
            data-testid={`lead-detail-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="ld-content">
        {tab === "details" && <DetailsTab lead={lead} staffList={staffList} me={me} onEdit={() => setModal("edit")} />}
        {tab === "timeline" && <TimelineTab events={timeline} />}
        {tab === "followups" && (
          <FollowupsTab
            followups={filteredFollowups}
            filter={fuFilter}
            setFilter={setFuFilter}
            onAdd={() => setModal("followup")}
            onUpdate={async (fid, patch) => {
              try {
                await api.patch(`/leads/${leadId}/followups/${fid}`, patch);
                toast.success("Follow-up updated");
                load();
              } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
            }}
            onDelete={async (fid) => {
              if (!window.confirm("Delete this follow-up?")) return;
              try {
                await api.delete(`/leads/${leadId}/followups/${fid}`);
                toast.success("Follow-up deleted");
                load();
              } catch (e) { toast.error("Delete failed"); }
            }}
          />
        )}
      </div>

      {/* Modals */}
      {modal === "call" && (
        <LogCallModal
          onClose={() => setModal(null)}
          onSubmit={async (payload) => {
            await api.post(`/leads/${leadId}/calls`, payload);
            toast.success("Call logged");
            setModal(null);
            load();
          }}
        />
      )}
      {modal === "note" && (
        <AddNoteModal
          onClose={() => setModal(null)}
          onSubmit={async (content) => {
            await api.post(`/leads/${leadId}/notes`, { content });
            toast.success("Note saved");
            setModal(null);
            load();
          }}
        />
      )}
      {modal === "followup" && (
        <AddFollowupModal
          onClose={() => setModal(null)}
          onSubmit={async (payload) => {
            await api.post(`/leads/${leadId}/followups`, payload);
            toast.success("Follow-up scheduled");
            setModal(null);
            load();
          }}
        />
      )}
      {modal === "stage" && (
        <ChangeStageModal
          stages={stageLookups}
          current={lead.stage}
          onClose={() => setModal(null)}
          onSubmit={async ({ stage, reason }) => {
            await api.post(`/leads/${leadId}/change-stage`, { stage, reason });
            toast.success(`Sub-stage changed to ${stage}`);
            setModal(null);
            load();
          }}
        />
      )}
      {modal === "edit" && (
        <EditLeadModal
          lead={lead}
          onClose={() => setModal(null)}
          onSubmit={async (patch) => {
            await api.patch(`/leads/${leadId}`, patch);
            toast.success("Lead updated");
            setModal(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ---------- Sub-components ----------

const CommRow = ({ icon, cls, label, count }) => (
  <div className="ld-comm-row">
    <div className={`ld-comm-icon ${cls}`}>{icon}</div>
    <span className="ld-comm-label">{label}</span>
    <span className={`ld-comm-count ${!count ? "zero" : ""}`}>{count || 0}</span>
  </div>
);

const TelRow = ({ color, label, count }) => (
  <div className="ld-tel-row">
    <div className={`ld-tel-dot ${color}`} />
    <span className="ld-tel-label">{label}</span>
    <span className={`ld-tel-count ${!count ? "zero" : ""}`}>{count || 0}</span>
  </div>
);

function DetailsTab({ lead, staffList = [], me, onEdit }) {
  const assignedName = staffList.find((s) => s.id === lead.assigned_to)?.name || (lead.assigned_to ? "—" : "Unassigned");
  const canEdit = me?.role === "admin" || lead.assigned_to === me?.id;
  const basic = [
    ["Full Name", lead.name],
    ["Email", lead.email, "accent"],
    ["Mobile", lead.phone],
    ["Alternate Mobile", lead.alt_phone],
    ["City", lead.city],
    ["State", lead.state],
  ];
  const course = [
    ["Course", lead.course_interest],
    ["Education Level", lead.education_level],
    ["Career Goal", lead.career_goal],
    ["Budget", lead.budget],
  ];
  const info = [
    ["Lead Sub-Stage", lead.stage, "accent"],
    ["Lead Stage", lead.category],
    ["Lead Source", SOURCE_LABEL[lead.source] || lead.source],
    ["Assigned To", assignedName],
    ["Created On", fmtIST(lead.created_at)],
    ["Last Active", fmtIST(lead.updated_at)],
  ];
  const customEntries = Object.entries(lead.custom_fields || {}).filter(([k]) => k && k.trim());
  return (
    <>
      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button className="ld-btn-save" onClick={onEdit} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", fontSize: 13 }}>
            <Edit3 size={13} /> Edit Lead
          </button>
        </div>
      )}
      <Section title="Basic Information" rows={basic} testid="section-basic" />
      <Section title="Course & Interest" rows={course} testid="section-course" />
      <Section title="Lead Info" rows={info} testid="section-info" />
      {customEntries.length > 0 && (
        <div className="ld-section-card" data-testid="section-custom">
          <div className="ld-section-title">Custom Fields</div>
          <div className="ld-details-grid">
            {customEntries.map(([k, v]) => (
              <div className="ld-detail-row" key={k}>
                <div className="ld-detail-label" style={{ textTransform: "none" }}>{k.replace(/_/g, " ")}</div>
                <div className={`ld-detail-value ${v ? "" : "na"}`}>{v || "Not provided"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

const Section = ({ title, rows, testid }) => (
  <div className="ld-section-card" data-testid={testid}>
    <div className="ld-section-title">{title}</div>
    <div className="ld-details-grid">
      {rows.map(([label, value, cls]) => (
        <div className="ld-detail-row" key={label}>
          <div className="ld-detail-label">{label}</div>
          <div className={`ld-detail-value ${value ? (cls || "") : "na"}`}>
            {value || "Not provided"}
          </div>
        </div>
      ))}
    </div>
  </div>
);

function TimelineTab({ events }) {
  if (!events.length) {
    return <div className="ld-empty" data-testid="timeline-empty">No activity yet.</div>;
  }
  return (
    <div className="ld-section-card" style={{ padding: "0 20px" }} data-testid="lead-detail-timeline">
      {events.map((e, i) => (
        <TimelineEntry event={e} key={i} isLast={i === events.length - 1} />
      ))}
    </div>
  );
}

const ICON_CLASS_BY_TYPE = {
  created: "created", stage_change: "stage", category_change: "category",
  call: "call", note: "note", followup_completed: "followup",
  followup_created: "followup", reassign: "assign",
};

const ICON_BY_TYPE = {
  created: <PlusCircle size={14} />,
  stage_change: <TrendingUp size={14} />,
  category_change: <Tag size={14} />,
  call: <Phone size={14} />,
  note: <Edit3 size={14} />,
  followup_completed: <Check size={14} />,
  followup_created: <Calendar size={14} />,
  reassign: <UserCheck size={14} />,
};

function TimelineEntry({ event, isLast }) {
  const cls = ICON_CLASS_BY_TYPE[event.type] || "created";
  const iconEl = ICON_BY_TYPE[event.type] || <PlusCircle size={14} />;
  const meta = event.meta || {};
  const delta = Number(event.score_delta || 0);
  const scoreCls = delta > 0 ? "pos" : delta < 0 ? "neg" : "neutral";
  const scoreText = delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : "—";
  return (
    <div className="ld-tl-entry">
      <div className="ld-tl-left">
        <div className={`ld-tl-icon ${cls}`}>{iconEl}</div>
        {!isLast && <div className="ld-tl-line" />}
      </div>
      <div className="ld-tl-body">
        <div className="ld-tl-header-row">
          <div className="ld-tl-desc"><strong>{event.by || "System"}</strong> {stripBy(event.message, event.by)}</div>
          <div className={`ld-tl-score ${scoreCls}`}>{scoreText}</div>
        </div>
        {event.type === "call" && (
          <div className="ld-tl-call-chips">
            <span className="ld-call-chip">📞 {meta.call_type === "inbound" ? "Inbound" : "Outbound"}</span>
            {meta.source === "mcube" && <span className="ld-call-chip">☎️ MCube</span>}
            {meta.source === "mcube" && meta.dialstatus === "INITIATED" && (
              <span className="ld-call-chip">⏳ Dialing…</span>
            )}
            {meta.source === "mcube" && meta.dialstatus && meta.dialstatus !== "INITIATED" && (
              <span className="ld-call-chip">{meta.dialstatus}</span>
            )}
            {meta.duration_minutes > 0 && (
              <span className="ld-call-chip">⏱ {durationDisplay(meta.duration_minutes)}</span>
            )}
            {meta.outcome && (
              <span className="ld-call-chip">{OUTCOME_EMOJI[meta.outcome] || "•"} {OUTCOME_LABELS[meta.outcome] || meta.outcome}</span>
            )}
            {meta.recording_url && (
              <a href={meta.recording_url} target="_blank" rel="noreferrer" className="ld-call-chip ld-recording-link">
                <Play size={10} /> Recording
              </a>
            )}
          </div>
        )}
        {event.type === "call" && meta.notes && (
          <div className="ld-tl-box" style={{ borderLeft: "3px solid #EA580C" }}>&ldquo;{meta.notes}&rdquo;</div>
        )}
        {event.type === "note" && meta.content && (
          <div className="ld-tl-box" style={{ borderLeft: "3px solid #9333EA" }}>&ldquo;{meta.content}&rdquo;</div>
        )}
        {event.type === "followup_completed" && meta.notes && (
          <div className="ld-tl-box" style={{ borderLeft: "3px solid #16A34A" }}>&ldquo;{meta.notes}&rdquo;</div>
        )}
        {(event.type === "stage_change" || event.type === "category_change") && meta.from && meta.to && (
          <div className="ld-tl-stage-change">
            <span className="ld-from-stage">{meta.from}</span>
            <span className="ld-arrow">→</span>
            <span className="ld-to-stage">{meta.to}</span>
          </div>
        )}
        <div className="ld-tl-time">{fmtIST(event.at).replace(", ", " · ")}</div>
      </div>
    </div>
  );
}

const stripBy = (msg, by) => {
  if (!msg) return "";
  if (by && msg.startsWith(by + " ")) return msg.slice(by.length + 1);
  return msg;
};

function FollowupsTab({ followups, filter, setFilter, onAdd, onUpdate, onDelete }) {
  const [openMenuId, setOpenMenuId] = useState(null);
  return (
    <>
      <div className="ld-fu-header">
        <div className="ld-fu-filters">
          {[
            ["all", "All"], ["upcoming", "Upcoming"], ["pending", "Pending"], ["completed", "Completed"],
          ].map(([id, label]) => (
            <button
              key={id}
              className={`ld-filter-chip ${filter === id ? "active" : ""}`}
              onClick={() => setFilter(id)}
              data-testid={`followup-filter-${id}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button className="ld-add-fu-btn" onClick={onAdd} data-testid="followup-add-btn">
          <Plus size={12} /> Add Follow-up
        </button>
      </div>

      {followups.length === 0 && (
        <div className="ld-empty" data-testid="followups-empty">No follow-ups yet.</div>
      )}
      {followups.map((f) => (
        <div className="ld-fu-card" key={f.id} data-testid={`followup-card-${f.id}`}>
          <div className="ld-fu-icon-wrap">
            {f.status === "completed" ? "✅" : f.status === "pending" ? "🕐" : "📞"}
          </div>
          <div className="ld-fu-content">
            <div className="ld-fu-row1">
              <div>
                <div className="ld-fu-title">{f.subject}</div>
                {f.description && <div className="ld-fu-subject">{f.description}</div>}
              </div>
              <span className={`ld-status-pill ${f.status}`}>
                {f.status.charAt(0).toUpperCase() + f.status.slice(1)}
              </span>
            </div>
            <div className="ld-fu-meta">
              <span className="ld-fu-meta-item">👤 {f.by || "—"}</span>
              <span className="ld-fu-meta-item">
                📅 {fmtIST(f.start_at)}{f.end_at ? ` – ${fmtIST(f.end_at)}` : ""}
              </span>
            </div>
            {f.notes && <div className="ld-fu-desc">{f.notes}</div>}
          </div>
          <div className="ld-fu-menu-wrap">
            <button
              className="ld-dots-btn"
              onClick={() => setOpenMenuId(openMenuId === f.id ? null : f.id)}
              data-testid={`followup-menu-${f.id}`}
            >
              <MoreHorizontal size={16} />
            </button>
            {openMenuId === f.id && (
              <div className="ld-fu-menu">
                {f.status !== "completed" && (
                  <button onClick={() => { onUpdate(f.id, { status: "completed" }); setOpenMenuId(null); }} data-testid={`followup-complete-${f.id}`}>
                    Mark Completed
                  </button>
                )}
                {f.status === "completed" && (
                  <button onClick={() => { onUpdate(f.id, { status: "pending" }); setOpenMenuId(null); }}>
                    Reopen
                  </button>
                )}
                <button onClick={() => { onDelete(f.id); setOpenMenuId(null); }} className="danger" data-testid={`followup-delete-${f.id}`}>
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

// ---------- Modals ----------

const ModalShell = ({ title, children, onClose, testid }) => (
  <div className="ld-modal-overlay open" onClick={onClose} data-testid={testid}>
    <div className="ld-modal" onClick={(e) => e.stopPropagation()}>
      <div className="ld-modal-header">
        <div className="ld-modal-title">{title}</div>
        <button className="ld-close-btn" onClick={onClose} data-testid="modal-close-btn">
          <X size={16} />
        </button>
      </div>
      {children}
    </div>
  </div>
);

function LogCallModal({ onClose, onSubmit }) {
  const [type, setType] = useState("outbound");
  const [outcome, setOutcome] = useState("interested");
  const [duration, setDuration] = useState("");
  const [when, setWhen] = useState(() => new Date().toISOString().slice(0, 16));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        call_type: type,
        outcome,
        duration_minutes: parseFloat(duration) || 0,
        when_at: when ? new Date(when).toISOString() : new Date().toISOString(),
        notes: notes.trim(),
      };
      await onSubmit(payload);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Log Call" onClose={onClose} testid="modal-log-call">
      <div className="ld-form-group">
        <label className="ld-form-label">Call Type</label>
        <select value={type} onChange={(e) => setType(e.target.value)} className="ld-form-select" data-testid="call-type">
          <option value="outbound">Outbound</option>
          <option value="inbound">Inbound</option>
        </select>
      </div>
      <div className="ld-form-group">
        <label className="ld-form-label">Outcome</label>
        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="ld-form-select" data-testid="call-outcome">
          {Object.entries(OUTCOME_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>
      <div className="ld-form-row">
        <div className="ld-form-group">
          <label className="ld-form-label">Duration (minutes)</label>
          <input type="number" min="0" step="0.5" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 5.5" className="ld-form-input" data-testid="call-duration" />
        </div>
        <div className="ld-form-group">
          <label className="ld-form-label">Date &amp; Time</label>
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="ld-form-input" data-testid="call-when" />
        </div>
      </div>
      <div className="ld-form-group">
        <label className="ld-form-label">Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Call summary…" className="ld-form-textarea" data-testid="call-notes" />
      </div>
      <div className="ld-modal-footer">
        <button className="ld-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="ld-btn-save" onClick={save} disabled={saving} data-testid="call-save">
          {saving ? "Saving…" : "Save Call"}
        </button>
      </div>
    </ModalShell>
  );
}

function AddNoteModal({ onClose, onSubmit }) {
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!content.trim()) return toast.error("Note content required");
    setSaving(true);
    try { await onSubmit(content.trim()); } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title="Add Note" onClose={onClose} testid="modal-add-note">
      <div className="ld-form-group">
        <label className="ld-form-label">Note</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What happened? Any context worth remembering?"
          className="ld-form-textarea"
          rows={5}
          data-testid="note-content"
        />
      </div>
      <div className="ld-modal-footer">
        <button className="ld-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="ld-btn-save" onClick={save} disabled={saving} data-testid="note-save">
          {saving ? "Saving…" : "Save Note"}
        </button>
      </div>
    </ModalShell>
  );
}

function AddFollowupModal({ onClose, onSubmit }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [startAt, setStartAt] = useState(() => {
    const t = new Date(); t.setDate(t.getDate() + 1); t.setHours(10, 0, 0, 0);
    return t.toISOString().slice(0, 16);
  });
  const [endAt, setEndAt] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!subject.trim()) return toast.error("Subject required");
    if (!startAt) return toast.error("Start date/time required");
    setSaving(true);
    try {
      await onSubmit({
        subject: subject.trim(),
        description: description.trim(),
        start_at: new Date(startAt).toISOString(),
        end_at: endAt ? new Date(endAt).toISOString() : null,
        notes: notes.trim(),
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title="Add Follow-up" onClose={onClose} testid="modal-add-followup">
      <div className="ld-form-group">
        <label className="ld-form-label">Subject</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Fee follow-up" className="ld-form-input" data-testid="fu-subject" />
      </div>
      <div className="ld-form-group">
        <label className="ld-form-label">Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short one-liner" className="ld-form-input" data-testid="fu-description" />
      </div>
      <div className="ld-form-row">
        <div className="ld-form-group">
          <label className="ld-form-label">Start</label>
          <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="ld-form-input" data-testid="fu-start" />
        </div>
        <div className="ld-form-group">
          <label className="ld-form-label">End (optional)</label>
          <input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} className="ld-form-input" data-testid="fu-end" />
        </div>
      </div>
      <div className="ld-form-group">
        <label className="ld-form-label">Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember before the call?" className="ld-form-textarea" data-testid="fu-notes" />
      </div>
      <div className="ld-modal-footer">
        <button className="ld-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="ld-btn-save" onClick={save} disabled={saving} data-testid="fu-save">
          {saving ? "Saving…" : "Schedule Follow-up"}
        </button>
      </div>
    </ModalShell>
  );
}

function ChangeStageModal({ stages, current, onClose, onSubmit }) {
  const [stage, setStage] = useState(current || (stages[0]?.name || ""));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!stage) return toast.error("Pick a stage");
    if (stage === current) return toast.info("Stage unchanged");
    setSaving(true);
    try { await onSubmit({ stage, reason }); } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title="Change Sub-Stage" onClose={onClose} testid="modal-change-stage">
      <div className="ld-form-group">
        <label className="ld-form-label">New Sub-Stage</label>
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="ld-form-select" data-testid="stage-select">
          {(stages || []).map((s) => (
            <option key={s.id || s.name} value={s.name}>{s.name}</option>
          ))}
        </select>
      </div>
      <div className="ld-form-group">
        <label className="ld-form-label">Reason (optional)</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why the move?" className="ld-form-textarea" data-testid="stage-reason" />
      </div>
      <div className="ld-modal-footer">
        <button className="ld-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="ld-btn-save" onClick={save} disabled={saving} data-testid="stage-save">
          {saving ? "Saving…" : "Update Stage"}
        </button>
      </div>
    </ModalShell>
  );
}

function EditLeadModal({ lead, onClose, onSubmit }) {
  const [form, setForm] = useState({
    name: lead.name || "",
    email: lead.email || "",
    phone: lead.phone || "",
    alt_phone: lead.alt_phone || "",
    city: lead.city || "",
    state: lead.state || "",
    course_interest: lead.course_interest || "",
    education_level: lead.education_level || "",
    career_goal: lead.career_goal || "",
    budget: lead.budget || "",
    notes: lead.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await onSubmit(form); } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); setSaving(false); }
  };
  const Field = ({ label, k, type = "text", as }) => (
    <div className="ld-form-group">
      <label className="ld-form-label">{label}</label>
      {as === "select" ? (
        <select className="ld-form-input" value={form[k]} onChange={(e) => set(k, e.target.value)}>
          <option value="">— Not provided —</option>
          {(k === "course_interest" ? COURSES : k === "education_level" ? EDUCATION_LEVELS : CAREER_GOALS).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      ) : as === "textarea" ? (
        <textarea className="ld-form-textarea" value={form[k]} onChange={(e) => set(k, e.target.value)} rows={3} />
      ) : (
        <input className="ld-form-input" type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} />
      )}
    </div>
  );
  return (
    <ModalShell title="Edit Lead" onClose={onClose} testid="modal-edit-lead">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Field label="Full Name" k="name" />
        <Field label="Email" k="email" type="email" />
        <Field label="Mobile" k="phone" />
        <Field label="Alternate Mobile" k="alt_phone" />
        <Field label="City" k="city" />
        <Field label="State" k="state" />
        <Field label="Course" k="course_interest" as="select" />
        <Field label="Education Level" k="education_level" as="select" />
        <Field label="Career Goal" k="career_goal" as="select" />
        <Field label="Budget" k="budget" />
      </div>
      <Field label="Notes" k="notes" as="textarea" />
      <div className="ld-modal-footer">
        <button className="ld-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="ld-btn-save" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </ModalShell>
  );
}
